/**
 * Auto-fill overlay — the read-path bridge that makes document extractions
 * COUNT.
 *
 * D4 in the extraction defect ledger: non-professional extractions write only
 * `provenanceDetail.autoFillFields` on the requirement's primary section, and
 * `resolveReportFields` never read it. So a successfully extracted flood zone
 * sat in the SED contributing exactly zero to completeness, readiness or the
 * coverage matrix — the machine did the work and the gauges ignored it.
 *
 * WHY AN OVERLAY AND NOT MATERIALIZATION. Doc-only keys (`bfe_ft`,
 * `serviceDate`, `parcelId`, …) have no SED leaf by design — materializing
 * them would demand a SED migration and a second write path to keep honest.
 * Reading at resolve time is additive, needs no migration, and lands at the
 * single completeness chokepoint (`evaluateSedFieldCompleteness →
 * resolveReportFields`) that feeds requirement status Tier 2, coverage Tier 1
 * and readiness — one rung, every gauge. The professional materialization
 * path (`geotechnical.professionalFacts.*` etc.) is deliberately untouched;
 * it already counts through aligned SED reads.
 *
 * REVIEWS ARE LAW HERE. A field the user REJECTED returns `undefined` — a
 * value a human has called wrong must not keep scoring. (`corrected` values
 * live in `autoFillFields` already, so they flow through normally; `accepted`
 * changes nothing about strength, per the review contract.)
 */
import type { SiteEvidenceDocument, StructuredEvidenceValue } from '../../types/siteEvidence';
import { REQUIREMENT_TO_SECTION_MAP } from '../../config/requirementToSection';
import type { FieldReview } from './extractedFieldReview';
import { siteResourceViewFromSed } from '../sed/siteResourceDocumentProjection';

// These six worksheet roles never alias municipal production/treatment rates.
const SCOPED_WATER_ROLES = [
  'projectAverageWaterDemandMgd', 'projectPeakWaterDemandMgd', 'projectWaterMarginMgd',
  'siteWastewaterAllocationMgd', 'projectPeakWastewaterDischargeMgd', 'projectWastewaterMarginMgd',
] as const;

interface OverlaySlot {
  provenanceDetail?: {
    autoFillFields?: Record<string, StructuredEvidenceValue>;
    autoFillFieldSources?: Record<string, { docId: string; value: StructuredEvidenceValue }>;
    fieldReviews?: Record<string, FieldReview>;
    docId?: string;
  };
}

function primarySlot(
  sed: SiteEvidenceDocument | null | undefined,
  requirementId: string,
): OverlaySlot | undefined {
  if (!sed) return undefined;
  const sectionId = REQUIREMENT_TO_SECTION_MAP[requirementId]?.[0];
  if (!sectionId) return undefined;
  return (sed as unknown as Record<string, OverlaySlot | undefined>)[sectionId];
}

export interface AutoFillOverlayHit {
  value: StructuredEvidenceValue;
  /** The document that stamped the value, when recorded. */
  docId?: string;
}

/**
 * The extracted value for one schema key, unless a human rejected it.
 *
 * Returns `undefined` for: no SED, unmapped requirement, section absent,
 * nothing extracted for the key, or a `rejected` review. Empty-string values
 * are treated as absent — an extractor writing `''` has not found anything,
 * and counting it would recreate the vacuous-truth bug.
 */
export function resolveAutoFillOverlayField(
  sed: SiteEvidenceDocument | null | undefined,
  requirementId: string,
  schemaKey: string,
): AutoFillOverlayHit | undefined {
  const detail = primarySlot(sed, requirementId)?.provenanceDetail;
  if (detail?.fieldReviews?.[schemaKey]?.decision === 'rejected') return undefined;
  const value = detail?.autoFillFields?.[schemaKey];
  if (requirementId === 'municipal-utility-letter'
    && SCOPED_WATER_ROLES.some(key => key === schemaKey)) {
    // CORRECT keeps the existing explicit human-authored override contract.
    // ACCEPT alone never upgrades a loose machine scalar into source authority.
    if (detail?.fieldReviews?.[schemaKey]?.decision === 'corrected'
      && typeof value === 'number' && Number.isFinite(value) && value >= 0) return { value };
    // Existing SEDs already retain these literal, role-qualified source rows.
    // Read that closed ledger without rewriting section data or the SED body.
    const fact = siteResourceViewFromSed(sed)?.facts.find(row => row.key === schemaKey);
    return fact ? { value: fact.value } : undefined;
  }
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;
  if (Array.isArray(value) && value.length === 0) return undefined;
  const source = detail?.autoFillFieldSources?.[schemaKey];
  return { value, docId: source && JSON.stringify(source.value) === JSON.stringify(value) ? source.docId : detail?.docId };
}

/** Does this requirement have ANY countable overlay value at all? */
export function hasAutoFillOverlay(
  sed: SiteEvidenceDocument | null | undefined,
  requirementId: string,
): boolean {
  const detail = primarySlot(sed, requirementId)?.provenanceDetail;
  const keys = [...Object.keys(detail?.autoFillFields ?? {}),
    ...(requirementId === 'municipal-utility-letter' ? SCOPED_WATER_ROLES : [])];
  return keys.some(key => resolveAutoFillOverlayField(sed, requirementId, key) !== undefined);
}
